import clsx from "clsx";
import { useMemo } from "react";
import Masonry from "react-masonry-css";
import { Waypoint } from "react-waypoint";

import { ErrorRetry } from "@/features/common/components/ErrorRetry";
import { useBreakpoints } from "@/features/common/hooks/useBreakpoint";
import { EditPhotoButtonGroup } from "@/features/modelPhoto/components/EditPhotoButtonGroup";
import { EmptyModelPhotoList } from "@/features/modelPhoto/components/EmptyModelPhotoList";
import { ModelPhoto } from "@/features/modelPhoto/components/ModelPhoto";
import { PrivatePreviewButton } from "@/features/modelPhoto/components/PrivatePreviewButton";
import { useEditModelAlbumFormDialogContext } from "@/features/modelPhoto/contexts/useEditModelAlbumFormContext";
import { useModelPhotoList } from "@/features/modelPhoto/hooks/useModelPhotoList";
import {
  AccessTypeEnum,
  ModelWithPrivateInfoFragment,
  useModelPrivatePreviewPhotoQuery,
} from "@/generated/graphql-hooks";
import { getPublicConfig } from "@/helpers/getPublicConfig";
import DownIcon from "@/icons/down.svg";

const {
  features: {
    model: { itemsPerQuery },
  },
} = getPublicConfig();

type ModelPhotoListProps = {
  columns?: number;
  model: ModelWithPrivateInfoFragment;
  previewPrivate?: boolean;
  accessType: AccessTypeEnum;
};

export const ModelPhotoList = ({
  columns = 3,
  model,
  accessType,
  previewPrivate = false,
}: ModelPhotoListProps) => {
  const { breakpoints, currentBreakpoint } = useBreakpoints();
  const { mode } = useEditModelAlbumFormDialogContext();
  const {
    modelPhotos,
    emptyModelPhoto,
    error,
    isLoading,
    isFetching,
    hasNextPage,
    fetchMore,
    refetch,
  } = useModelPhotoList(model?.userId, accessType);
  const { data: { modelPrivatePreviewPhoto } = {} } =
    useModelPrivatePreviewPhotoQuery({ modelUserId: model.userId });

  const isEmpty = useMemo(
    () =>
      (emptyModelPhoto || (!isFetching && !!error)) &&
      model?.canAccessPrivate &&
      !previewPrivate,
    [
      emptyModelPhoto,
      model?.canAccessPrivate,
      isFetching,
      error,
      previewPrivate,
    ],
  );

  return (
    <div className="flex flex-col">
      <Masonry
        breakpointCols={{
          default: columns,
          [breakpoints.sm]: 2,
        }}
        className={clsx(isEmpty && "justify-center", "-ml-2 flex")}
        columnClassName="pl-2"
      >
        {!!previewPrivate && !!modelPrivatePreviewPhoto && (
          <PrivatePreviewButton
            model={model}
            modelPrivatePreviewPhoto={modelPrivatePreviewPhoto}
          />
        )}

        {!!isLoading &&
          Array.from(Array(itemsPerQuery)).map((_, index) => (
            <div
              className="mb-2 aspect-square animate-pulse rounded-lg bg-slate-200"
              key={index}
            ></div>
          ))}

        {emptyModelPhoto && !model?.canAccessPrivate && !error && (
          <EmptyModelPhotoList accessType={accessType} />
        )}

        {!isFetching && !modelPhotos?.length && !!error && (
          <ErrorRetry refetch={refetch} error={error}/>
        )}

        {modelPhotos?.map((data) => (
          <ModelPhoto alt={model.username} modelPhoto={data} key={data.id} />
        ))}

        {!isLoading &&
          isFetching &&
          Array.from(Array(itemsPerQuery)).map((_, index) => (
            <div
              className="mb-2 aspect-square animate-pulse rounded-lg bg-slate-200"
              key={index}
            ></div>
          ))}

        {!isFetching && !!modelPhotos?.length && !!error && (
          <ErrorRetry refetch={refetch} error={error}/>
        )}
      </Masonry>

      {mode === "edit" && <EditPhotoButtonGroup accessType={accessType} />}

      {currentBreakpoint === "sm" && hasNextPage && !error ? (
        <button
          className="text-primary mt-4 flex items-center justify-center text-base"
          onClick={fetchMore}
        >
          <div>查看更多</div>
          <DownIcon className="pt-0.5" />
        </button>
      ) : (
        <Waypoint onEnter={fetchMore} />
      )}
    </div>
  );
};
